import * as React from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { FieldArray, formValueSelector, reduxForm } from 'redux-form';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import { FormFieldArrayStatus } from '@acceligentllc/shared/enums/formFieldArrayStatus';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import { WORK_ORDER_FORM } from 'af-constants/reduxForms';

import * as ReportTypeActions from 'af-actions/reportType';

import type { OwnProps as ReportTypeArrayProps } from './ReportTypeArray';
import ReportTypeArray from './ReportTypeArray';
import type WorkOrderFM from '../../formModel';
import Section from '../../Shared/Section';

interface OwnProps {
	resetTimer: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<WorkOrderFM>;

class ReportTypes extends React.PureComponent<Props> {

	static mapUpdatedReportTypes = (wort: WorkOrderFM['workOrderReportTypeLookup'][0], index: number) => {
		return {
			...wort,
			reportTypeLookup: {
				...wort.reportTypeLookup,
				index,
			},
			status: wort.status === FormFieldArrayStatus.OLD ? FormFieldArrayStatus.EDITED : wort.status,
		};
	};

	async componentDidMount() {
		const { loadRequiredReportTypes, change, isCreate } = this.props;
		const requiredReportTypes = await loadRequiredReportTypes();
		if (isCreate) {
			change(
				'workOrderReportTypeLookup',
				requiredReportTypes.
					sort((_rtA, _rtB) => {
						if (!_rtA.defaultIndex) {
							return -1;
						}
						if (!_rtB.defaultIndex) {
							return 1;
						}
						return _rtA.defaultIndex - _rtB.defaultIndex;
					}).
					map((_rt, _index) => ({ status: 'ADDED', reportTypeLookup: { reportTypeId: _rt.id, name: _rt.name, isRequired: true, index: _index } }))
			);
		}
	}

	onDragEnd = ({ source, destination }: DropResult) => {
		const { selector, change } = this.props;

		if (!destination) {
			return;
		}

		const reportTypes = selector('workOrderReportTypeLookup') as WorkOrderFM['workOrderReportTypeLookup'];

		if (source.index === destination?.index) {
			return;
		}

		const [movedItem] = reportTypes.splice(source.index, 1);

		reportTypes.splice(destination?.index, 0, movedItem);

		const updatedReportTypes = reportTypes.map(ReportTypes.mapUpdatedReportTypes);

		change('workOrderReportTypeLookup', updatedReportTypes);
	};

	renderDroppable = () => {
		const { resetTimer, selector, loadReportTypes, change, hasReorderReportTypesPermission, disabled } = this.props;
		return (
			<Droppable direction="vertical" droppableId="report-type-lookup">
				{(droppableProvided) => (
					<div ref={droppableProvided.innerRef}>
						<FieldArray<ReportTypeArrayProps>
							change={change}
							component={ReportTypeArray}
							disabled={disabled}
							hasReorderReportTypesPermission={hasReorderReportTypesPermission}
							isEditable={!disabled}
							loadReportTypes={loadReportTypes}
							name="workOrderReportTypeLookup"
							resetTimer={resetTimer}
							selector={selector}
						/>
					</div>
				)}
			</Droppable>
		);
	};

	render() {
		return (
			<Section
				label="Field Report Types"
				tooltip="The list of Report Types that will be included for submitting in the Field Report. Report Types that are greyed out are required and cannot be removed from the list. Additional Report Types can be added on the Work Order. All Report Types on the list can be reordered."
			>
				<Col md={8}>
					<DragDropContext onDragEnd={this.onDragEnd}>
						{this.renderDroppable()}
					</DragDropContext>
				</Col>
			</Section>
		);
	}
}

const selector = formValueSelector(WORK_ORDER_FORM);

function mapStateToProps(state: RootState) {
	const { workOrder, user: { companyData, userData } } = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const hasReorderReportTypesPermission = isAllowed(
		PagePermissions.COMPANY.FIELD_REPORT.REORDER_RTS,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	return {
		hasReorderReportTypesPermission,
		isCreate: !workOrder.order?.id,
		disabled: workOrder.order?.status === WorkOrderStatus.LOCKED
			|| workOrder.order?.status === WorkOrderStatus.CANCELED
			|| (workOrder.order?.isPaused ?? false),
		selector: (fieldName: string) => selector(state, fieldName),
	};
}

function mapDispatchToProps() {
	return {
		loadReportTypes: ReportTypeActions.findCustomShallowActiveList,
		loadRequiredReportTypes: ReportTypeActions.findRequiredShallowActiveList,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());
const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	reduxForm<WorkOrderFM>({ form: WORK_ORDER_FORM })
);

export default enhance(ReportTypes);
